import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';

export const DELETE_REASONS = [
  { code: 'access_denied', label: 'Access denied' },
  { code: 'customer_cancelled', label: 'Customer cancelled' },
  { code: 'duplicate', label: 'Duplicate stop' },
  { code: 'wrong_address', label: 'Wrong address' },
  { code: 'not_required', label: 'Not required' },
  { code: 'other', label: 'Other' },
];

/**
 * Capture a reason before soft-deleting a route stop. Leaves an audit trail
 * (who + why). Deleted stops stay visible (struck through) and can be restored.
 */
export const DeleteStopDialog = ({ open, onOpenChange, jobId, stopId, stopLabel, api, onDeleted }) => {
  const [reasonCode, setReasonCode] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReasonCode('');
      setReasonNote('');
    }
  }, [open]);

  const handleDelete = async () => {
    if (!reasonCode) { toast.error('Please select a reason'); return; }
    if (reasonCode === 'other' && !reasonNote.trim()) { toast.error('Please describe the reason'); return; }
    setSaving(true);
    try {
      const res = await api.delete(`/api/jobs/${jobId}/stops/${stopId}`, {
        data: { reason_code: reasonCode, reason_note: reasonNote.trim() || null },
      });
      onDeleted(res.data.stops, res.data.route_summary);
      toast.success('Stop deleted');
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete stop');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] max-w-md" data-testid="delete-stop-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--m-text)]">Delete stop{stopLabel ? ` — ${stopLabel}` : ''}</DialogTitle>
          <DialogDescription className="text-[var(--m-text-secondary)] text-xs">
            Log a reason for deleting this stop. It stays visible (struck through) for the dispatcher and can be restored.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Reason *</label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" data-testid="delete-stop-reason-select">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {DELETE_REASONS.map((r) => (
                  <SelectItem key={r.code} value={r.code} data-testid={`delete-stop-reason-${r.code}`}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">
              {reasonCode === 'other' ? 'Describe the reason *' : 'Additional notes (optional)'}
            </label>
            <textarea
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
              placeholder="Add any detail for the audit trail..."
              rows={3}
              className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-md px-3 py-2 text-[var(--m-text)] text-sm focus:outline-none focus:border-[#ffd108]"
              data-testid="delete-stop-reason-note"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button className="bg-red-600 hover:bg-red-700 text-[var(--m-text)]" onClick={handleDelete} disabled={saving} data-testid="delete-stop-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1" /> Delete Stop</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteStopDialog;
